"use strict";
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PhotoPainter = void 0;
var PathBasedPainter_1 = require("../../PathBasedPainter");
var executeSvgPath_1 = require("../../../util/executeSvgPath");
var round_1 = require("../../../util/round");
var DEFAULT_PNG_COMPRESSION = 6;
/**
 * Implements drawing-related operations for photos. Supports filters, frames, cropping,
 * clipping and alpha masks. Intended for photo editing applications.
 */
var PhotoPainter = /** @class */ (function (_super) {
    __extends(PhotoPainter, _super);
    /**
     * @param loadImage An image loading function for the current environment.
     * @param filterBackend An image filtering implementation.
     * @param getLoadingPlaceholder Provides an image to display while `photo.url` is being loaded.
     * @param getEmptyPlaceholder Provides an image to display if `photo.url` is `null`.
     */
    function PhotoPainter(loadImage, getLoadingPlaceholder, getEmptyPlaceholder, filterBackend) {
        var _this = _super.call(this, "photo") || this;
        _this.loadImage = loadImage;
        _this.getLoadingPlaceholder = getLoadingPlaceholder;
        _this.getEmptyPlaceholder = getEmptyPlaceholder;
        _this.filterBackend = filterBackend;
        /**
         * Keys used to store and retrieve resources in the {@link ObjectResourceStore}.
         */
        _this._keys = {
            image: {
                source: "image:source",
                filtered: "image:filtered"
            },
            mask: "mask",
            frame: "frame"
        };
        _this.baseWidthOf = function (object) { return object.width; };
        _this.baseHeightOf = function (object) { return object.height; };
        return _this;
    }
    PhotoPainter.prototype.loadResources = function (store, object) {
        object.url
            ? this._loadAndFilterImage(store, object)
            : store.discard(this._keys.image.source, this._keys.image.filtered);
        object.maskUrl
            ? this._loadMask(store, object)
            : store.discard(this._keys.mask);
        object.frameUrl
            ? this._loadFrame(store, object)
            : store.discard(this._keys.frame);
    };
    /**
     * Loads and processes the main image. Throws if `object.url` is missing.
     */
    PhotoPainter.prototype._loadAndFilterImage = function (store, object) {
        var _this = this;
        if (!object.url) {
            throw new Error("Internal error: attempted to load image for object without `url`.");
        }
        store
            .load(this._keys.image.source, function () { return _this.loadImage(object.url); }, { hash: object.url })
            .pipe(this._keys.image.filtered, function (i) { return _this._applyFilters(i, store, object); }, { hash: this._hashFilters(object) });
    };
    /**
     * Computes a hash to detect if filters have changed.
     */
    PhotoPainter.prototype._hashFilters = function (object) {
        return this.filterBackend.hashFilters(object);
    };
    /**
     * Applies the filters specified on the object to the image.
     */
    PhotoPainter.prototype._applyFilters = function (image, store, object) {
        var getDestination = function () { return store.canvasFor("filtering", image, "bitmap"); }; // lazy for performance reasons
        return this.filterBackend.applyFilters(image, object, getDestination);
    };
    /**
     * Loads and processes the mask image. Throws if `object.maskUrl` is missing.
     */
    PhotoPainter.prototype._loadMask = function (store, object) {
        var _this = this;
        if (!object.maskUrl) {
            throw new Error("Internal error: attempted to load mask for object without `maskUrl`.");
        }
        store.load(this._keys.mask, function () { return _this.loadImage(object.maskUrl); }, { hash: object.maskUrl });
    };
    /**
     * Loads and processes the frame image. Throws if `object.frameUrl` is missing.
     */
    PhotoPainter.prototype._loadFrame = function (store, object) {
        var _this = this;
        if (!object.frameUrl) {
            throw new Error("Internal error: attempted to load frame for object without `frameUrl`.");
        }
        store.load(this._keys.frame, function () { return _this.loadImage(object.frameUrl); }, { hash: object.frameUrl });
    };
    PhotoPainter.prototype.tracePath = function (ctx, store, object) {
        if (object.clipPaths.length) {
            this._traceCustomPath(ctx, object);
        }
        else {
            this._traceRectanglePath(ctx, object);
        }
    };
    PhotoPainter.prototype.needsMiterOvershoot = function (object) {
        return object.clipPaths.length > 0;
    };
    PhotoPainter.prototype.canWriteSvg = function (object) {
        return !object.shadow && !object.stroke;
    };
    /**
     * Traces a custom SVG path, such as a circle or a star shape.
     */
    PhotoPainter.prototype._traceCustomPath = function (ctx, object) {
        var e_1, _a;
        ctx.save();
        // Scale path to match object size:
        ctx.scale(this.scaledBaseWidthOf(object) / object.clipViewBoxWidth, this.scaledBaseHeightOf(object) / object.clipViewBoxHeight);
        ctx.beginPath();
        try {
            for (var _b = __values(object.clipPaths), _c = _b.next(); !_c.done; _c = _b.next()) {
                var clipPath = _c.value;
                executeSvgPath_1.executeSvgPath(clipPath, ctx);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
            }
            finally { if (e_1) throw e_1.error; }
        }
        ctx.closePath();
        ctx.restore();
    };
    /**
     * Traces the default rectangular path.
     */
    PhotoPainter.prototype._traceRectanglePath = function (ctx, object) {
        ctx.beginPath();
        ctx.rect(0, 0, round_1.round(object, this.scaledBaseWidthOf(object)), round_1.round(object, this.scaledBaseHeightOf(object)));
        ctx.closePath();
    };
    /**
     * Paints the image, or a loader if the image is loading or failed to load.
     */
    PhotoPainter.prototype.paintFill = function (store, object) {
        this._paintFillOn(store.layers.result.ctx, store, object);
    };
    PhotoPainter.prototype.writeSvg = function (ctx, store, object) {
        var _a;
        ctx.save();
        var bitmap = store.layers.child("bitmap", "bitmap");
        // Ensure sharpness is retained by disabling antialiasing.
        bitmap.ctx.imageSmoothingEnabled = false;
        // Ensure the background color is white (canvas implementations may
        // translate [0, 0, 0, 0] transparent into [0, 0, 0] black)
        if (((_a = object.svgOptions) === null || _a === void 0 ? void 0 : _a.encoding) === "jpeg") {
            bitmap.ctx.fillStyle = "white";
            bitmap.ctx.fillRect(0, 0, bitmap.canvas.width, bitmap.canvas.height);
        }
        this._paintFillOn(bitmap.ctx, store, object, "bitmap");
        var _b = this._toBufferParameters(object.svgOptions), encoding = _b.encoding, config = _b.config;
        var base64 = bitmap.canvas
            // @ts-ignore
            .toBuffer(encoding, config)
            .toString("base64");
        var dataUrl = "data:" + encoding + ";base64," + base64;
        var width = bitmap.canvas.width;
        var height = bitmap.canvas.height;
        var svg = "\n            <svg xmlns:xlink=\"http://www.w3.org/1999/xlink\"\n                xmlns=\"http://www.w3.org/2000/svg\"\n                viewBox=\"0 0 " + width + " " + height + "\" \n                height=\"" + height + "\" \n                width=\"" + width + "\">\n                <defs>\n                    <image xlink:href=\"" + dataUrl + "\" height=\"" + height + "\" width=\"" + width + "\" y=\"0\" x=\"0\" id=\"" + object.id + "\">\n                    </image>\n                </defs>\n                <use transform=\"matrix(1, 0, 0, 1, 0, 0)\" xlink:href=\"#" + object.id + "\"></use>\n            </svg>";
        ctx.unsafeInsertSvg(svg);
        ctx.restore();
    };
    PhotoPainter.prototype._toBufferParameters = function (svgOptions) {
        var _a, _b;
        if (!svgOptions) {
            return {
                encoding: "image/png",
                config: {
                    compressionLevel: DEFAULT_PNG_COMPRESSION
                }
            };
        }
        switch (svgOptions.encoding) {
            case "jpeg":
                return {
                    encoding: "image/jpeg",
                    config: {
                        quality: (_a = svgOptions.quality) !== null && _a !== void 0 ? _a : 0.8
                    }
                };
            case "png":
                return {
                    encoding: "image/png",
                    config: {
                        compressionLevel: (_b = svgOptions.compression) !== null && _b !== void 0 ? _b : DEFAULT_PNG_COMPRESSION
                    }
                };
            default:
                throw new Error("Unsupported encoding " + svgOptions.encoding + ", supported: jpeg, png");
        }
    };
    PhotoPainter.prototype._paintFillOn = function (destination, store, object, canvasType) {
        var shouldRenderShadow = object.shadow && !object.stroke;
        var ctx = shouldRenderShadow
            ? store.layers.child("pre-shadow", canvasType).clear().ctx
            : destination;
        object.maskUrl && ctx.save();
        this._applyMask(ctx, store, object);
        this._paintImage(ctx, store, object);
        object.maskUrl && ctx.restore();
        this._paintFrame(ctx, store, object);
        if (shouldRenderShadow) {
            this.applyShadowSettings(destination, object);
            store.layers.composite("pre-shadow", destination);
            this.removeShadowSettings(destination);
        }
    };
    /**
     * Applies the alpha mask to subsequent draw operations.
     */
    PhotoPainter.prototype._applyMask = function (ctx, store, object) {
        if (object.maskUrl) {
            var mask = store.require("mask");
            if (mask.status === "loaded") {
                this._paintOverlayImage(ctx, store, mask.result, object, { clip: true });
                ctx.globalCompositeOperation = "source-in";
            }
        }
    };
    /**
     * Paints the photo frame image, stretched to match the object's dimensions.
     */
    PhotoPainter.prototype._paintFrame = function (ctx, store, object) {
        if (object.frameUrl) {
            var frame = store.require("frame");
            if (frame.status === "loaded") {
                this._paintOverlayImage(ctx, store, frame.result, object, { clip: false });
            }
        }
    };
    /**
     * Paints the inner image or a placeholder, depending on the object's values and what
     * resources are currently loaded.
     */
    PhotoPainter.prototype._paintImage = function (ctx, store, object) {
        if (!object.url) {
            this._paintOverlayImage(ctx, store, this.getEmptyPlaceholder(object), object, { clip: true });
            return;
        }
        var filteredImage = store.require("image:filtered");
        if (filteredImage.status === "loaded") {
            this._paintInnerImage(ctx, store, filteredImage.result, object);
            return;
        }
        var sourceImage = store.require("image:source");
        if (sourceImage.status === "loaded") {
            this._paintInnerImage(ctx, store, sourceImage.result, object);
            return;
        }
        this._paintOverlayImage(ctx, store, this.getLoadingPlaceholder(object), object, { clip: true });
    };
    /**
     * Paints the inner image with cropping, clipping, inner angle/offset/zoom and flipping applied.
     */
    PhotoPainter.prototype._paintInnerImage = function (ctx, store, image, object) {
        ctx.save();
        this.clipPath(ctx, store, object);
        var _a = this.getInnerSize(object, image), innerWidth = _a.width, innerHeight = _a.height;
        var canRound = this._canRound(object);
        ctx.translate(round_1.round(canRound, 0.5 * this.scaledBaseWidthOf(object)), round_1.round(canRound, 0.5 * this.scaledBaseHeightOf(object)));
        ctx.rotate(object.innerAngle);
        ctx.scale(object.flipX ? -1 : 1, object.flipY ? -1 : 1);
        var offsetX = object.innerOffsetX * object.scaleX * object.innerZoom;
        var offsetY = object.innerOffsetY * object.scaleY * object.innerZoom;
        // Improves rendering for `image:filtered` due to type being HTMLCanvasElement
        // By default HTMLImageElement already renders in this quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(image, round_1.round(canRound, -0.5 * innerWidth + offsetX), round_1.round(canRound, -0.5 * innerHeight + offsetY), round_1.round(canRound, innerWidth), round_1.round(canRound, innerHeight));
        ctx.restore();
    };
    /**
     * Rounding combined with fit mode "contain" results in the image
     * being positioned 3-4 px too far to the right (or bottom) within
     * its subcanvas. This is because of rounding inconsistencies
     * that arise during positioning (with a {@link Device} scale
     * applied to the subcanvas). This proved difficult to fix, as a
     * workaround we disable rounding in that scenario.
     */
    PhotoPainter.prototype._canRound = function (object) {
        return object.round && object.fit !== "contain";
    };
    /**
     * Paints an image stretched to the object's dimensions.
     */
    PhotoPainter.prototype._paintOverlayImage = function (ctx, store, image, object, options) {
        ctx.save();
        if (options.clip) {
            this.clipPath(ctx, store, object);
        }
        ctx.drawImage(image, 0, 0, round_1.round(object, this.scaledBaseWidthOf(object)), round_1.round(object, this.scaledBaseHeightOf(object)));
        ctx.restore();
    };
    /**
     * Calculates the resolution at which the uncropped image should be rendered, accounting for
     * inner zoom, inner angle and scale X/Y.
     */
    PhotoPainter.prototype.getInnerSize = function (object, imageSize) {
        var objectSize = this.scaledBaseSizeOf(object);
        var rotatedImageSize = this._scaleWithAngleConstrained(imageSize, object.innerAngle);
        var rotatedObjectSize = this._scaleWithAngle(objectSize, object.innerAngle);
        var imageWidthToHeightFactor = rotatedImageSize.height / rotatedImageSize.width;
        var objectWidthToHeightFactor = rotatedObjectSize.height / rotatedObjectSize.width;
        var isImageWiderAspectRatioThanObject = objectWidthToHeightFactor > imageWidthToHeightFactor;
        var useWidthAsBase = object.fit === "contain"
            ? !isImageWiderAspectRatioThanObject
            : isImageWiderAspectRatioThanObject;
        var innerWidth = useWidthAsBase
            ? rotatedObjectSize.height / imageWidthToHeightFactor * object.innerZoom
            : rotatedObjectSize.width * object.innerZoom;
        return {
            width: innerWidth,
            height: innerWidth * imageWidthToHeightFactor
        };
    };
    ;
    /**
     * Scales a size as it rotates to ensure it continues to fill its "upright" bounds.
     *
     * Illustrative GIF: https://fotofabriek-cra.visualstudio.com/Common/_wiki?pageId=164
     */
    PhotoPainter.prototype._scaleWithAngle = function (size, angle) {
        var cos = Math.abs(Math.cos(angle));
        var sin = Math.abs(Math.sin(angle));
        return {
            width: size.height * sin + size.width * cos,
            height: size.height * cos + size.width * sin
        };
    };
    /**
     * Like {@link _scaleWithAngle}, but maintains the aspect ratio.
     *
     * Illustrative GIF: https://fotofabriek-cra.visualstudio.com/Common/_wiki?pageId=164
     */
    PhotoPainter.prototype._scaleWithAngleConstrained = function (size, angle) {
        var rotatedSize = this._scaleWithAngle(size, angle);
        var maxScale = Math.max(rotatedSize.width / size.width, rotatedSize.height / size.height);
        return {
            width: size.width * maxScale,
            height: size.height * maxScale
        };
    };
    PhotoPainter.prototype.shouldRepaint = function (previousData, newData) {
        var e_2, _a;
        var keys = [
            "url", "innerOffsetX", "innerOffsetY", "innerZoom", "innerAngle",
            "filter", "contrast", "saturation", "brightness",
            "clipPaths", "clipViewBoxWidth", "clipViewBoxHeight",
            "flipX", "flipY"
        ];
        try {
            for (var keys_1 = __values(keys), keys_1_1 = keys_1.next(); !keys_1_1.done; keys_1_1 = keys_1.next()) {
                var key = keys_1_1.value;
                if (newData[key] !== previousData[key]) {
                    return true;
                }
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (keys_1_1 && !keys_1_1.done && (_a = keys_1.return)) _a.call(keys_1);
            }
            finally { if (e_2) throw e_2.error; }
        }
        return _super.prototype.shouldRepaint.call(this, previousData, newData);
    };
    PhotoPainter.prototype.paintOperationSizesOf = function (object, store) {
        if (!store.contains("image:source")) {
            throw new Error("Required resources have not been loaded.");
        }
        var sourceImage = store.require("image:source");
        if (sourceImage.status !== "loaded") {
            throw new Error("Required resources have not been loaded.");
        }
        var paintSize = this.paintSizeOf(object);
        var innerSize = this.getInnerSize(object, sourceImage.result);
        return [paintSize, innerSize];
    };
    return PhotoPainter;
}(PathBasedPainter_1.PathBasedPainter));
exports.PhotoPainter = PhotoPainter;
//# sourceMappingURL=PhotoPainter.js.map